import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import { RootState } from 'store';

import { Close } from '@mui/icons-material';
import ClearIcon from '@mui/icons-material/Clear';
import {
  Autocomplete,
  Box,
  Drawer,
  FormHelperText,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';

import CustomButton from 'components/Button';
import { Header } from 'components/GlobalStyles/styles';
import LabelledDatePicker from 'components/LabelledDatePicker';
import LabelledTextField from 'components/LabelledTextField';
import SearchActivitiesModal from 'components/SearchActivities';
import { MessageType } from 'components/SnackBar';
import { BillingCycle } from 'features/NewJob/types';
import { CustomCheckbox, CustomSwitch } from 'features/UserDetail/styles';
import DisabledTextField from 'pages/ProjectManagementOverview/component/DisabledText';
import {
  useAddJobMutation,
  useGetManagerListQuery,
  useGetProjectByIdQuery,
  useGetProjectScopeQuery,
  useGetTeamsQuery,
  useUpdateJobMutation,
} from 'services/busmanApi';
import { AllowableActivitiesType, JobRequest } from 'types/JobTypes';
import { ProjectScopeResponse } from 'types/ProjectTypes';
import { ActivitiesProps } from 'types/WorkLogTypes';
import { DESCRIPTION_LIMIT, JOB_PRIORITY, JOB_TYPE } from 'utils/constants';
import { AddEditJobSchema, JobFormValues } from 'utils/schema';

import { ColContainer, FormSection, RowContainer } from '../../ProjectManagement/styles';

interface JobDrawerProps {
  initialValues: JobRequest | null;
  open: boolean;
  isEditing?: boolean;
  onClose: () => void;
  setSnackBarMessage: (message: MessageType) => void;
}

const PROJECT_STATUS = {
  inprogress: { name: 'In Progress', value: 'InProgress' },
  // completed: 'Completed',
  // billed: 'Billed',
};

const BILLING_CYCLES: BillingCycle[] = [
  { period: 'Weekly', days: 7 },
  { period: 'Fortnightly', days: 14 },
  { period: 'Monthly', days: 30 },
];

const defaultJobValue = {
  job_id: '',
  job_associated_quote: '',
  client_id: '',
  job_manager: '',
  teams: null,
  activities: [],
  invoice_header: '',
  job_content_type: '',
  job_description: '',
  job_status: '',
  job_name: '',
  job_type: '',
  job_modified: false,
  is_active: true,
  start_date: null,
  end_date: null,
  introduced_in_scope_id: '',
  removed_in_scope: '',
  percent_complete: 0,
  priority: '',
};

const JobDrawer: React.FC<JobDrawerProps> = ({ initialValues, open, onClose, isEditing, setSnackBarMessage }) => {
  const theme = useTheme();
  const { projectID } = useParams();
  const { user } = useSelector((state: RootState) => state.auth);
  useGetTeamsQuery(user?.user_id);

  const [addJob, { isLoading: isAddingJob }] = useAddJobMutation();
  const [updateJob] = useUpdateJobMutation();

  const { data: selectedProject } = useGetProjectByIdQuery(projectID);
  const { data: projectScopeList } = useGetProjectScopeQuery({ projectId: projectID });
  const { data: jobManagers } = useGetManagerListQuery({});
  const { activities } = useSelector((state: RootState) => state.worklogs);

  const [openAddActivities, setAddActivities] = React.useState(false);

  const scopeList: ProjectScopeResponse[] =
    projectScopeList?.filter((item) => item.status === 'DRAFT' || 'PENDING_APPROVAL') || [];

  const [allowableActivities, setAllowableActivities] = React.useState<AllowableActivitiesType[]>(
    initialValues?.activities ?? [],
  );

  let filteredActivities: ActivitiesProps[] = activities;

  if (allowableActivities.length) {
    filteredActivities = activities.filter((activity) => {
      return !allowableActivities.some(
        (allowedActivity: AllowableActivitiesType) => allowedActivity.activityId === activity.activity_id,
      );
    });
  }

  const activityIsActive = (id: string) => {
    const selectedActivity = activities.find((activity: ActivitiesProps) => activity.activity_id === id);

    return selectedActivity?.is_active;
  };

  const removeActivity = (id: string) => {
    const newActivities = allowableActivities.filter((activity: AllowableActivitiesType) => activity.activityId !== id);
    setAllowableActivities(newActivities);
  };

  const onSelectActivity = (userActivities: ActivitiesProps[]) => {
    const selectedActivities = userActivities.map((activity) => {
      return {
        activityId: activity.activity_id,
        activityName: activity.activity_name,
      };
    });
    setAllowableActivities((prevActivities) => [...prevActivities, ...selectedActivities]);
  };

  const {
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    defaultValues: initialValues ?? {
      ...defaultJobValue,
      client_id: selectedProject?.client_contact.client.client_id,
      job_manager: selectedProject?.manager[0].user_id,
      job_status: PROJECT_STATUS.inprogress.value,
      job_type: JOB_TYPE.TIMEBILLED,
      job_billing_cycle: BILLING_CYCLES[0].period,
      priority: JOB_PRIORITY.LOW,
    },
    resolver: zodResolver(AddEditJobSchema),
  });

  const managers = jobManagers || [];

  const onSubmit = async (data: JobFormValues) => {
    const payload = {
      ...data,
      activities: allowableActivities?.map((activity) => activity.activityId),
      job_number: data.job_name,
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    delete (payload as any).job_name;

    if (isEditing) {
      const result = await updateJob({ clientId: data.client_id, jobUpdates: payload });
      if ('error' in result) {
        setSnackBarMessage({ type: 'error', msg: `Error updating job due to, ${result.error}` });
        return;
      }

      setSnackBarMessage({ type: 'success', msg: 'Job updated successfully.' });
    } else {
      const result = await addJob(payload);

      if ('error' in result) {
        setSnackBarMessage({ type: 'error', msg: `Error adding job due to, ${result.error}` });
        return;
      }

      setSnackBarMessage({ type: 'success', msg: 'Job added successfully.' });
    }

    onClose();
  };

  const projectDuration =
    watch('start_date') && watch('end_date')
      ? dayjs(watch('end_date') as string).diff(dayjs(watch('start_date') as string), 'd')
      : 0;

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description">
        <Box
          role="presentation"
          sx={{ borderRadius: 2, width: { xs: '100vw', sm: 480 }, maxWidth: '100vw', p: { xs: 2, sm: 3 } }}>
          <Header>
            <Typography variant="h4">{isEditing ? 'Edit Job' : 'New Job'}</Typography>
            <Box onClick={onClose} sx={{ cursor: 'pointer' }}>
              <Close sx={{ color: 'text.disabled', fontSize: '18px' }} />
            </Box>
          </Header>

          <FormSection>
            <RowContainer>
              <LabelledTextField dataTestId="job-input-name" name="job_name" label="JOB NAME" control={control} />
            </RowContainer>

            <RowContainer>
              <ColContainer>
                <Typography variant="body2">PROJECT SCOPE</Typography>
                <Controller
                  name="introduced_in_scope_id"
                  control={control}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <ColContainer>
                        <Autocomplete
                          size="small"
                          options={scopeList}
                          value={scopeList.find((scope) => scope.id === field.value) ?? null}
                          getOptionLabel={(option) => `${option.version} — ${option.status}`}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          onChange={(event, value) => {
                            field.onChange(value?.id || '');
                            setValue('introduced_in_scope_id', value?.id || '');
                          }}
                          data-testid="job-select-scope"
                          ListboxProps={{
                            style: {
                              fontSize: '12px',
                            },
                          }}
                          renderInput={(params) => <TextField {...params} placeholder="Select scope" />}
                        />
                        {error && <FormHelperText sx={{ color: 'red' }}>*{error.message || error.type}</FormHelperText>}
                      </ColContainer>
                    );
                  }}
                />
              </ColContainer>
            </RowContainer>

            <RowContainer>
              <ColContainer>
                <Typography variant="body2">TYPE</Typography>
                <Select
                  size="small"
                  value={watch('job_type')}
                  data-testid="job-select-type"
                  onChange={(e) => setValue('job_type', e.target.value, { shouldValidate: true })}>
                  {Object.entries(JOB_TYPE).map(([key, value]) => (
                    <MenuItem key={key} value={value}>
                      {value}
                    </MenuItem>
                  ))}
                </Select>
              </ColContainer>
              <ColContainer>
                <Typography variant="body2">STATUS</Typography>
                <Select
                  size="small"
                  value={watch('job_status')}
                  data-testid="job-select-status"
                  sx={{
                    width: '100%',
                  }}
                  onChange={(e) => setValue('job_status', e.target.value, { shouldValidate: true })}>
                  {Object.entries(PROJECT_STATUS).map(([key, value]) => (
                    <MenuItem key={key} value={value.value}>
                      {value.name}
                    </MenuItem>
                  ))}
                </Select>
              </ColContainer>
            </RowContainer>

            <RowContainer>
              <RowContainer sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body1">ACTIVE JOB</Typography>
                <CustomSwitch
                  value={watch('is_active')}
                  onChange={(e) => e.target.checked}
                  data-testid="job-switch-active"
                />
              </RowContainer>
              <ColContainer>
                <Typography variant="body2">JOB PRIORITY</Typography>
                <Controller
                  name="priority"
                  control={control}
                  render={({ fieldState: { error } }) => {
                    return (
                      <ColContainer>
                        <Select
                          size="small"
                          value={watch('priority')}
                          data-testid="job-select-priority"
                          onChange={(e) => setValue('priority', e.target.value, { shouldValidate: true })}>
                          {Object.entries(JOB_PRIORITY).map(([key, value]) => (
                            <MenuItem key={key} value={value}>
                              {value}
                            </MenuItem>
                          ))}
                        </Select>
                        {error && <FormHelperText sx={{ color: 'red' }}>*{error.message || error.type}</FormHelperText>}
                      </ColContainer>
                    );
                  }}
                />
              </ColContainer>
            </RowContainer>

            <RowContainer>
              <LabelledTextField data-testid="job-input-number" name="job_name" label="JOB NUMBER" control={control} />
              <ColContainer>
                <Typography variant="body2">JOB MANAGER</Typography>
                <Controller
                  name="job_manager"
                  control={control}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <ColContainer>
                        <Autocomplete
                          size="small"
                          value={managers.find((manager) => manager.user_id === field.value) ?? null}
                          options={managers}
                          getOptionLabel={(option) => option.full_name}
                          isOptionEqualToValue={(option, value) => option.user_id === value.user_id}
                          onChange={(event, value) => setValue('job_manager', value?.user_id ?? '')}
                          data-testid="job-select-manager"
                          ListboxProps={{
                            style: {
                              fontSize: '12px',
                            },
                          }}
                          renderInput={(params) => <TextField {...params} placeholder="Select manager" />}
                        />
                        {error && <FormHelperText sx={{ color: 'red' }}>*{error.message || error.type}</FormHelperText>}
                      </ColContainer>
                    );
                  }}
                />
              </ColContainer>
            </RowContainer>

            <RowContainer>
              <LabelledTextField
                data-testid="job-input-description"
                name="job_description"
                label="DESCRIPTION"
                control={control}
                multiline
                placeholder="Text"
                rules={{ maxLength: DESCRIPTION_LIMIT }}
              />
            </RowContainer>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: -2 }}>
              <Typography variant="caption" sx={{ color: theme.palette.busmanColors.busmanRed }}>
                {`${(watch('job_description') || '').length}/${DESCRIPTION_LIMIT}`}
              </Typography>
            </Box>

            <RowContainer>
              <LabelledTextField
                data-testid="job-invoice-header"
                name="invoice_header"
                label="INVOICE HEADER"
                control={control}
                multiline
                placeholder="Text"
                rules={{ maxLength: DESCRIPTION_LIMIT }}
              />
            </RowContainer>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: -2 }}>
              <Typography variant="caption" sx={{ color: theme.palette.busmanColors.busmanRed }}>
                {`${(watch('invoice_header') || '').length}/${DESCRIPTION_LIMIT}`}
              </Typography>
            </Box>

            <Box sx={{ my: 2 }}>
              <Typography variant="h5">TIME</Typography>
              <RowContainer>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 2 }}>
                  <RowContainer>
                    <LabelledDatePicker
                      dataTestId="task-date-start"
                      name="start_date"
                      label="START (QUOTED)"
                      control={control}
                    />
                    <LabelledDatePicker
                      dataTestId="task-date-end"
                      name="end_date"
                      label="FINISH (QUOTED)"
                      control={control}
                    />
                  </RowContainer>
                  <RowContainer sx={{ mt: 1 }}>
                    <ColContainer>
                      <DisabledTextField title="DURATION" displayValue={projectDuration.toString()} />
                    </ColContainer>
                    <ColContainer>
                      <LabelledTextField name="percent_complete" label="% COMPLETE" control={control} />
                    </ColContainer>
                  </RowContainer>
                </Box>
              </RowContainer>
            </Box>

            <Box sx={{ my: 2 }}>
              <Typography variant="h5">BILLING</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, marginTop: 2 }}>
                <ColContainer>
                  <Typography sx={{ mb: 1 }} variant="body2">
                    BILLING PERIOD
                  </Typography>
                  <Select
                    size="small"
                    value={watch('job_billing_cycle')}
                    data-testid="job-select-period"
                    onChange={(e) => setValue('job_billing_cycle', e.target.value, { shouldValidate: true })}>
                    {BILLING_CYCLES.map((billing) => (
                      <MenuItem key={billing.period} value={billing.period}>
                        {billing.period}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.job_billing_cycle && (
                    <FormHelperText sx={{ color: 'red' }}>
                      *{errors.job_billing_cycle.message || errors.job_billing_cycle.type}
                    </FormHelperText>
                  )}
                </ColContainer>

                <ColContainer>
                  <DisabledTextField
                    title="NO. DAYS IN BILLING CYCLE"
                    displayValue={
                      BILLING_CYCLES.find((b) => b.period === watch('job_billing_cycle'))?.days.toString() || '0'
                    }
                  />
                </ColContainer>
              </Box>
            </Box>

            <Box sx={{ my: 2 }}>
              <Typography variant="h5">ALLOWABLE ACTIVITIES</Typography>

              <Box sx={{ display: 'flex', flexDirection: 'row', gap: 4, marginTop: 2 }}>
                <ColContainer>
                  <List
                    sx={{
                      width: '100%',
                      maxHeight: '40vh',
                      overflowY: 'auto',
                      border: allowableActivities.length ? '' : `1px solid ${theme.palette.background.default}`,
                    }}>
                    {allowableActivities.length > 0 ? (
                      allowableActivities.map((activity: AllowableActivitiesType) => (
                        <ListItem
                          key={activity.activityId}
                          sx={{
                            fontSize: '12px',
                            fontWeight: '500',
                            height: '36px',
                            border: `1px solid ${theme.palette.background.default}`,
                            mb: 0.5,
                            ':hover': { bgcolor: theme.palette.background.default },
                          }}
                          secondaryAction={
                            <>
                              <Tooltip title="active">
                                <CustomCheckbox
                                  size="small"
                                  sx={{
                                    '& .MuiSvgIcon-root': {
                                      color: activityIsActive(activity.activityId)
                                        ? theme.palette.busmanColors.busmanPrimary
                                        : theme.palette.primary.light,
                                      backgroundColor: activityIsActive(activity.activityId)
                                        ? theme.palette.busmanColors.busmanPrimary
                                        : theme.palette.primary.light,
                                    },
                                  }}
                                />
                              </Tooltip>
                              <Tooltip title="remove activity">
                                <IconButton
                                  edge="end"
                                  aria-label="delete"
                                  onClick={() => removeActivity(activity.activityId)}>
                                  <ClearIcon sx={{ fontSize: 'small' }} />
                                </IconButton>
                              </Tooltip>
                            </>
                          }>
                          <ListItemText primary={activity.activityName} />
                        </ListItem>
                      ))
                    ) : (
                      <ListItem sx={{ justifyContent: 'center' }}>No Activities</ListItem>
                    )}
                  </List>

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
                    <CustomButton
                      width="medium"
                      text="Assign Activity"
                      variant="contained_grey"
                      onClick={() => setAddActivities(true)}
                    />
                  </Box>
                </ColContainer>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
              <CustomButton
                width="medium"
                text={isEditing ? 'Update Task' : 'Add Task'}
                variant="contained"
                disabled={isAddingJob}
                onClick={handleSubmit(onSubmit)}
                customSx={{
                  backgroundColor: theme.palette.busmanColors.busmanGreen,
                  color: theme.palette.common.white,
                  '&:hover': {
                    backgroundColor: theme.palette.busmanColors.busmanGreen,
                    opacity: 0.9,
                  },
                }}
              />
            </Box>
          </FormSection>
        </Box>
      </Drawer>
      <SearchActivitiesModal
        activities={filteredActivities}
        open={openAddActivities}
        onClose={() => setAddActivities(false)}
        onSelectMultiple={onSelectActivity}
      />
    </>
  );
};

export default JobDrawer;
